import type { VehicleSummary } from '@/types'

import { useEffect, useId, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import {
    CarFront,
    ChevronDown,
    Minus,
    Plus,
    Search,
    SearchX,
    Users,
    X
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonClass } from '@/lib/ui'
import { pluralize } from '@/lib/format'
import { normalizeText } from '@/lib/text'
import { scrollToSection } from '@/lib/scroll'
import { sortByRecommendation } from '@/lib/vehicle-helpers'
import VehicleCard from '@/components/vehicles/vehicle-card'

interface VehicleCatalogProps {
    vehicles: Array<VehicleSummary>
    onReserve: (vehicle: VehicleSummary) => void
}

interface Filters {
    category: string | null
    occasion: string | null
    passengers: number
    term: string
}

interface FilterPillProps {
    label: string
    count: number
    active: boolean
    disabled?: boolean
    onClick: () => void
}

interface PassengerStepperProps {
    value: number
    max: number
    onChange: (value: number) => void
}

const PAGE_SIZE = 9

const SORT_OPTIONS = [
    { value: 'recomendados', label: 'Recomendados' },
    { value: 'precio-asc', label: 'Precio: de menor a mayor' },
    { value: 'precio-desc', label: 'Precio: de mayor a menor' },
    { value: 'plazas', label: 'Más plazas primero' }
] as const

type SortKey = typeof SORT_OPTIONS[number]['value']

const isSortKey = (value: string | null): value is SortKey => SORT_OPTIONS.some(option => option.value === value)

const comparePrice = (a: VehicleSummary, b: VehicleSummary, direction: 1 | -1): number => {
    if (a.precio_hora === null && b.precio_hora === null) return sortByRecommendation(a, b)
    if (a.precio_hora === null) return 1
    if (b.precio_hora === null) return -1

    return (a.precio_hora - b.precio_hora) * direction || sortByRecommendation(a, b)
}

const sortVehicles = (vehicles: Array<VehicleSummary>, sort: SortKey): Array<VehicleSummary> => {
    const sorted = [...vehicles]

    if (sort === 'precio-asc') return sorted.sort((a, b) => comparePrice(a, b, 1))
    if (sort === 'precio-desc') return sorted.sort((a, b) => comparePrice(a, b, -1))
    if (sort === 'plazas') return sorted.sort((a, b) => b.pasajeros - a.pasajeros || sortByRecommendation(a, b))

    return sorted.sort(sortByRecommendation)
}

const getSearchText = (vehicle: VehicleSummary): string => {
    return normalizeText([
        vehicle.marca,
        vehicle.modelo,
        vehicle.categoria,
        vehicle.motorizacion ?? '',
        vehicle.resumen ?? '',
        ...vehicle.equipamiento,
        ...vehicle.ideal_para
    ].join(' '))
}

const matchesFilters = (vehicle: VehicleSummary, filters: Filters, searchText: string, ignore?: 'category'): boolean => {
    if (ignore !== 'category' && filters.category && vehicle.categoria !== filters.category) return false
    if (filters.occasion && !vehicle.ideal_para.includes(filters.occasion)) return false
    if (vehicle.pasajeros < filters.passengers) return false
    if (filters.term && !searchText.includes(filters.term)) return false

    return true
}

// Distinct values in first-seen order, with how many entries carry each one.
const countValues = (values: Array<string>): Array<{ value: string, count: number }> => {
    const counts = new Map<string, number>()

    for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1)

    return Array.from(counts, ([value, count]) => ({ value, count }))
}

const FilterPill = ({ label, count, active, disabled = false, onClick }: FilterPillProps) => (
    <button
        type='button'
        aria-pressed={active}
        disabled={disabled}
        onClick={onClick}
        className={cn(
            'inline-flex h-10 shrink-0 items-center gap-2 rounded-full px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40',
            active ? 'bg-foreground text-background shadow-sm' : 'text-foreground/75 hover:bg-secondary hover:text-foreground'
        )}
    >
        {label}

        <span className={cn('min-w-5 rounded-full px-1.5 py-0.5 text-center text-[0.7rem] tabular-nums', active ? 'bg-background/15 text-background' : 'bg-secondary text-muted-foreground')}>
            {count}
        </span>
    </button>
)

const PassengerStepper = ({ value, max, onChange }: PassengerStepperProps) => {
    const labelId = useId()
    const stepButton = 'flex size-8 items-center justify-center rounded-full text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:bg-transparent'

    return (
        <div role='group' aria-labelledby={labelId} className='flex h-12 items-center justify-between gap-3 rounded-2xl border border-input bg-background pr-2 pl-4 md:min-w-[14rem]'>
            <span id={labelId} className='flex items-center gap-2 text-sm text-muted-foreground'>
                <Users className='size-4 text-gold' aria-hidden='true' />
                Pasajeros
            </span>

            <div className='flex items-center gap-1'>
                <button type='button' onClick={() => onChange(value - 1)} disabled={value <= 1} aria-label='Quitar un pasajero' className={stepButton}>
                    <Minus className='size-4' aria-hidden='true' />
                </button>

                <output aria-live='polite' className='w-7 text-center text-sm font-semibold tabular-nums'>
                    {value}
                </output>

                <button type='button' onClick={() => onChange(value + 1)} disabled={value >= max} aria-label='Añadir un pasajero' className={stepButton}>
                    <Plus className='size-4' aria-hidden='true' />
                </button>
            </div>
        </div>
    )
}

export default function VehicleCatalog({ vehicles, onReserve }: VehicleCatalogProps) {
    const router = useRouter()
    const searchId = useId()
    const sortId = useId()
    const [search, setSearch] = useState('')
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

    // Filters live in the address bar, so going back from a vehicle page keeps them and links can be shared.
    const readParam = (key: string): string | null => {
        const value = router.query[key]

        return typeof value === 'string' && value !== '' ? value : null
    }

    const maxPassengers = useMemo(() => vehicles.reduce((max, vehicle) => Math.max(max, vehicle.pasajeros), 1), [vehicles])
    const searchIndex = useMemo(() => new Map(vehicles.map(vehicle => [vehicle.id, getSearchText(vehicle)])), [vehicles])

    const category = readParam('tipo')
    const occasion = readParam('ocasion')
    const passengers = Math.min(Math.max(Number.parseInt(readParam('pasajeros') ?? '1', 10) || 1, 1), maxPassengers)
    const sortParam = readParam('orden')
    const sort: SortKey = isSortKey(sortParam) ? sortParam : 'recomendados'
    const term = normalizeText(search)

    const filters = useMemo<Filters>(() => ({ category, occasion, passengers, term }), [category, occasion, passengers, term])

    const filtered = useMemo(() => {
        const matching = vehicles.filter(vehicle => matchesFilters(vehicle, filters, searchIndex.get(vehicle.id) ?? ''))

        return sortVehicles(matching, sort)
    }, [vehicles, filters, searchIndex, sort])

    const categoryOptions = useMemo(() => {
        const pool = vehicles.filter(vehicle => matchesFilters(vehicle, filters, searchIndex.get(vehicle.id) ?? '', 'category'))
        const counts = new Map(countValues(pool.map(vehicle => vehicle.categoria)).map(({ value, count }) => [value, count]))

        return {
            total: pool.length,
            options: countValues(vehicles.map(vehicle => vehicle.categoria).filter(Boolean)).map(({ value }) => ({ value, count: counts.get(value) ?? 0 }))
        }
    }, [vehicles, filters, searchIndex])

    const occasions = useMemo(() => {
        return countValues(vehicles.flatMap(vehicle => vehicle.ideal_para))
            .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value, 'es'))
    }, [vehicles])

    useEffect(() => {
        setVisibleCount(PAGE_SIZE)
    }, [filters, sort])

    const updateQuery = (patch: Record<string, string | null>) => {
        const query: Record<string, string> = {}

        for (const [key, value] of Object.entries(router.query)) {
            if (typeof value === 'string') query[key] = value
        }

        for (const [key, value] of Object.entries(patch)) {
            if (value === null) delete query[key]
            else query[key] = value
        }

        void router.replace({ pathname: router.pathname, query }, undefined, { shallow: true, scroll: false })
    }

    const hasFilters = Boolean(category || occasion || passengers > 1 || term)

    const clearFilters = () => {
        setSearch('')
        updateQuery({ tipo: null, ocasion: null, pasajeros: null })
    }

    if (vehicles.length === 0) {
        return (
            <div className='mt-14 flex flex-col items-center rounded-3xl border border-dashed border-border bg-card/60 px-6 py-16 text-center sm:mt-16'>
                <span className='flex size-14 items-center justify-center rounded-full bg-secondary text-gold'>
                    <CarFront className='size-6' aria-hidden='true' />
                </span>

                <h2 className='mt-5 font-heading text-2xl text-foreground'>
                    Estamos preparando nuestra flota
                </h2>

                <p className='mt-2 max-w-md text-sm leading-relaxed text-muted-foreground'>
                    Muy pronto verás aquí todos nuestros vehículos. Mientras tanto, cuéntanos qué necesitas y te propondremos la mejor opción.
                </p>

                <button type='button' onClick={() => scrollToSection('reservar')} className={buttonClass('primary', 'md', 'mt-6')}>
                    Pedir una propuesta
                </button>
            </div>
        )
    }

    const visibleVehicles = filtered.slice(0, visibleCount)
    const remaining = filtered.length - visibleVehicles.length

    return (
        <div className='mt-14 sm:mt-16'>
            <div className='rounded-3xl border border-border/80 bg-card/80 p-3 shadow-[0_18px_40px_-30px_rgb(40_30_10/0.35)] backdrop-blur sm:p-4'>
                <div role='group' aria-label='Tipo de vehículo' className='no-scrollbar -mx-1 flex gap-1.5 overflow-x-auto px-1 py-0.5'>
                    <FilterPill
                        label='Todos'
                        count={categoryOptions.total}
                        active={!category}
                        onClick={() => updateQuery({ tipo: null })}
                    />

                    {categoryOptions.options.map(option => (
                        <FilterPill
                            key={option.value}
                            label={option.value}
                            count={option.count}
                            active={category === option.value}
                            disabled={option.count === 0 && category !== option.value}
                            onClick={() => updateQuery({ tipo: category === option.value ? null : option.value })}
                        />
                    ))}
                </div>

                <div className='mt-3 grid gap-3 border-t border-border/70 pt-3 md:grid-cols-[minmax(0,1fr)_auto_auto]'>
                    <div className='relative flex h-12 items-center rounded-2xl border border-input bg-background transition-shadow focus-within:border-gold/50 focus-within:ring-2 focus-within:ring-ring/30'>
                        <label htmlFor={searchId} className='sr-only'>
                            Buscar un vehículo
                        </label>

                        <Search className='pointer-events-none absolute left-4 size-4 text-muted-foreground' aria-hidden='true' />

                        <input
                            id={searchId}
                            type='search'
                            value={search}
                            onChange={event => setSearch(event.target.value)}
                            placeholder='Busca por marca, modelo o equipamiento'
                            autoComplete='off'
                            className='h-full w-full rounded-2xl bg-transparent pr-11 pl-11 text-sm text-foreground placeholder:text-muted-foreground/80 focus:outline-none [&::-webkit-search-cancel-button]:hidden'
                        />

                        {search && (
                            <button
                                type='button'
                                onClick={() => setSearch('')}
                                aria-label='Borrar la búsqueda'
                                className='absolute right-2 flex size-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                            >
                                <X className='size-4' aria-hidden='true' />
                            </button>
                        )}
                    </div>

                    <PassengerStepper
                        value={passengers}
                        max={maxPassengers}
                        onChange={value => updateQuery({ pasajeros: value > 1 ? String(value) : null })}
                    />

                    <div className='relative flex h-12 items-center rounded-2xl border border-input bg-background transition-shadow focus-within:border-gold/50 focus-within:ring-2 focus-within:ring-ring/30'>
                        <label htmlFor={sortId} className='sr-only'>
                            Ordenar vehículos
                        </label>

                        <select
                            id={sortId}
                            value={sort}
                            onChange={event => updateQuery({ orden: event.target.value === 'recomendados' ? null : event.target.value })}
                            className='h-full w-full cursor-pointer appearance-none rounded-2xl bg-transparent pr-11 pl-4 text-sm font-medium text-foreground focus:outline-none md:w-auto'
                        >
                            {SORT_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>

                        <ChevronDown className='pointer-events-none absolute right-4 size-4 text-muted-foreground' aria-hidden='true' />
                    </div>
                </div>
            </div>

            {occasions.length > 0 && (
                <div role='group' aria-label='Ideal para' className='mt-5 flex flex-wrap items-center gap-2'>
                    <span aria-hidden='true' className='mr-1 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground'>
                        Ideal para
                    </span>

                    {occasions.map(option => {
                        const active = occasion === option.value

                        return (
                            <button
                                key={option.value}
                                type='button'
                                aria-pressed={active}
                                onClick={() => updateQuery({ ocasion: active ? null : option.value })}
                                className={cn(
                                    'inline-flex h-9 items-center gap-1.5 rounded-full border px-3.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                    active ? 'border-foreground bg-foreground text-background' : 'border-border bg-card text-foreground/80 hover:border-gold/40 hover:text-foreground'
                                )}
                            >
                                {option.value}
                                {active && <X className='size-3.5' aria-hidden='true' />}
                            </button>
                        )
                    })}
                </div>
            )}

            <div className='mt-8 flex flex-wrap items-center justify-between gap-3'>
                <p aria-live='polite' className='text-sm text-muted-foreground'>
                    <span className='font-semibold text-foreground'>{filtered.length}</span>
                    {' '}
                    {hasFilters
                        ? `${pluralize(filtered.length, 'vehículo encaja', 'vehículos encajan')} con tu búsqueda`
                        : `${pluralize(filtered.length, 'vehículo', 'vehículos')} en nuestra flota`}
                </p>

                {hasFilters && (
                    <button type='button' onClick={clearFilters} className='inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium text-foreground/75 transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'>
                        <X className='size-4' aria-hidden='true' />
                        Quitar filtros
                    </button>
                )}
            </div>

            {filtered.length === 0
                ? (
                    <div className='mt-6 flex flex-col items-center rounded-3xl border border-dashed border-border bg-card/60 px-6 py-16 text-center'>
                        <span className='flex size-14 items-center justify-center rounded-full bg-secondary text-gold'>
                            <SearchX className='size-6' aria-hidden='true' />
                        </span>

                        <h2 className='mt-5 font-heading text-2xl text-foreground'>
                            Ningún vehículo encaja con esos filtros
                        </h2>

                        <p className='mt-2 max-w-md text-sm leading-relaxed text-muted-foreground'>
                            Prueba a quitar algún filtro o cuéntanos tu plan y te propondremos la mejor alternativa de nuestra flota.
                        </p>

                        <div className='mt-6 flex flex-wrap justify-center gap-3'>
                            <button type='button' onClick={clearFilters} className={buttonClass('primary')}>
                                Quitar filtros
                            </button>

                            <button type='button' onClick={() => scrollToSection('reservar')} className={buttonClass('outline')}>
                                Pedir una propuesta
                            </button>
                        </div>
                    </div>
                )
                : (
                    <ul className='mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                        {visibleVehicles.map((vehicle, index) => (
                            <li key={vehicle.id} className='animate-in fade-in slide-in-from-bottom-2 duration-500 motion-reduce:animate-none'>
                                <VehicleCard vehicle={vehicle} onReserve={onReserve} preload={index < 3} headingLevel='h2' />
                            </li>
                        ))}
                    </ul>
                )}

            {remaining > 0 && (
                <div className='mt-12 flex justify-center'>
                    <button type='button' onClick={() => setVisibleCount(count => count + PAGE_SIZE)} className={buttonClass('outline', 'lg')}>
                        Ver más vehículos
                        <span className='text-muted-foreground'>({remaining})</span>
                    </button>
                </div>
            )}
        </div>
    )
}
